(ns yardwerkz.web.jobs.list
  "Jobs list page with active/past tab filter using domain logic."
  (:require
   [uix.core :refer [defui $ use-state use-effect]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.realtime :as realtime]
   [yardwerkz.web.router :as router]
   [yardwerkz.web.domain.jobs :as jobs]
   [yardwerkz.web.ui.layout :as layout]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]))

;; =============================================================================
;; Status Badge
;; =============================================================================

(def ^:private status-badge-variants
  "Map job status display color to badge variant."
  {[:green 600] :success
   [:green 100] :success
   [:blue 500]  :info
   [:blue 100]  :info
   [:amber 500] :warning
   [:amber 100] :warning
   [:red 500]   :danger
   [:red 100]   :danger
   [:violet 500] :info
   [:violet 100] :info
   [:gray 500]  :default
   [:gray 100]  :default})

(defn- status->badge-variant [status]
  (let [{:keys [color-key]} (jobs/get-status-config status)]
    (get status-badge-variants color-key :default)))

;; =============================================================================
;; Job Card
;; =============================================================================

(defui job-card [{:keys [job]}]
  (let [{:keys [id human-id service address status date time price worker]} job
        schedule-text (cond
                        (and date time) (str date " \u2022 " time)
                        date date
                        time time
                        :else "Schedule TBD")]
    ($ ui/card {:class "p-4 hover:shadow-md transition-shadow cursor-pointer"
                :on-click #(router/navigate! :job {:id (str id)})}
       ;; Header: service name + status
       ($ :div {:class "flex items-center justify-between mb-2"}
          ($ :div
             ($ :p {:class "font-semibold text-gray-900"} service)
             (when human-id
               ($ :p {:class "text-xs text-gray-400 mt-0.5"} human-id)))
          ($ ui/badge {:variant (status->badge-variant status)}
             (:label (jobs/get-status-config status))))

       ;; Address
       (when (seq address)
         ($ :div {:class "flex items-center text-sm text-gray-500 mb-1"}
            ($ icons/MapPin {:size 14 :class "mr-1.5 flex-shrink-0"})
            ($ :span {:class "truncate"} address)))

       ;; Schedule
       ($ :div {:class "flex items-center text-sm text-gray-500 mb-3"}
          ($ icons/Calendar {:size 14 :class "mr-1.5 flex-shrink-0"})
          ($ :span schedule-text))

       ;; Footer: price + worker
       ($ :div {:class "flex items-center justify-between pt-2 border-t border-gray-100"}
          ($ :span {:class "text-lg font-bold text-green-600"}
             (or price "Quote pending"))
          (when worker
            ($ :div {:class "flex items-center text-sm"}
               ($ :span {:class "font-medium text-gray-700 mr-1"} (:name worker))
               (when (:rating worker)
                 ($ :span {:class "flex items-center text-gray-500"}
                    ($ icons/Star {:size 14 :class "text-amber-500 mr-0.5"})
                    (str (:rating worker))))))))))

;; =============================================================================
;; Page
;; =============================================================================

(defui list-page []
  (let [[tab set-tab] (use-state :active)
        customer-id (rfx/use-sub [::store/customer-id])
        {:keys [data loading?]} (realtime/use-customer-jobs customer-id)
        all-jobs (mapv jobs/transform-job (or data []))
        active-jobs (jobs/filter-active all-jobs)
        past-jobs (jobs/filter-past all-jobs)
        display-jobs (if (= tab :active) active-jobs past-jobs)]

    ($ layout/page-container
       ($ :h1 {:class "text-2xl font-bold text-gray-900 mb-6"} "My Jobs")
       ($ ui/tabs {:tabs [{:id :active :label (str "Active (" (count active-jobs) ")")}
                          {:id :past :label (str "Past (" (count past-jobs) ")")}]
                   :active tab
                   :on-change set-tab})
       ($ :div {:class "mt-6"}
          (if loading?
            ($ ui/skeleton-list {:count 3})
            (if (seq display-jobs)
              ($ :div {:class "space-y-4"}
                 (for [job display-jobs]
                   ($ job-card {:key (:id job) :job job})))
              ($ ui/empty-state
                 {:icon ($ icons/Briefcase {:size 48})
                  :title (if (= tab :active) "No active jobs" "No past jobs")
                  :description (if (= tab :active)
                                 "Book a service to get started!"
                                 "Your completed jobs will appear here.")
                  :action (when (= tab :active)
                            ($ ui/button {:on-click #(router/navigate! :book)}
                               "Book a Service"))})))))))
